/**
 * Utility functions for handling redirects with external URL support
 */

/**
 * Same rules as relativePathSchema in lib/auth/schemas (strict relative path only).
 * Used here to avoid pulling Zod into client bundle while keeping validation consistent.
 */
const RELATIVE_PATH_REGEX = /^\/[^\s\\]*$/;

/**
 * Get the external frontend URL from environment variable
 * Falls back to window.location.origin if not set
 */
export function getFrontendUrl(): string {
  if (typeof window !== 'undefined') {
    // Use environment variable if set, otherwise use current origin
    const externalUrl = process.env.NEXT_PUBLIC_FRONTEND_URL;
    if (externalUrl) {
      return externalUrl;
    }
    return window.location.origin;
  }
  // Server-side: use environment variable or default
  return process.env.NEXT_PUBLIC_FRONTEND_URL || 'http://localhost:3000';
}

/**
 * Get the full URL for a path, using external URL if configured
 */
export function getFullUrl(path: string): string {
  const baseUrl = getFrontendUrl();
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Get the return URL for OAuth flows
 * Uses external URL if configured
 */
export function getReturnUrl(path?: string): string {
  const baseUrl = getFrontendUrl();
  const returnPath = path || '/auth/callback';
  const normalizedPath = returnPath.startsWith('/') ? returnPath : `/${returnPath}`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Validate returnUrl to prevent Open Redirect attacks.
 * Based on relativePathSchema rules: only simple relative paths (e.g. /dashboard) are allowed.
 * Explicitly rejects absolute URLs (http://), protocol-relative (//), backslashes, and whitespace.
 */
export function isValidReturnUrl(url: string | null | undefined): boolean {
  if (url == null || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return (
    trimmed.length > 0 &&
    RELATIVE_PATH_REGEX.test(trimmed) &&
    !trimmed.startsWith('//') &&
    !/^https?:\/\//i.test(trimmed)
  );
}

/**
 * Build login URL with optional returnUrl (for redirect-back-after-login).
 */
export function getLoginUrl(returnUrl?: string | null): string {
  const base = '/login';
  if (isValidReturnUrl(returnUrl)) {
    return `${base}?returnUrl=${encodeURIComponent(returnUrl!)}`;
  }
  return base;
}

